import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  useTheme,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { motion } from 'framer-motion';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';

const MotionPaper = motion(Paper);

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Reset error state before attempting login
    setError(false);
    const success = login(password);
    if (success) {
      navigate('/');
    } else {
      setError(true);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
        isolation: 'isolate',
        position: 'relative',
        overflow: 'hidden',
        p: 2,
        '@keyframes floatCircle1': {
          '0%': { transform: 'translate(0, 0)' },
          '50%': { transform: 'translate(20px, -20px)' },
          '100%': { transform: 'translate(0, 0)' }
        },
        '@keyframes floatCircle2': {
          '0%': { transform: 'translate(0, 0)' },
          '50%': { transform: 'translate(-15px, 15px)' },
          '100%': { transform: 'translate(0, 0)' }
        },
        '@keyframes floatCircle3': {
          '0%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(10px, -10px) scale(1.1)' },
          '100%': { transform: 'translate(0, 0) scale(1)' }
        },
        '@keyframes rotateRing': {
          '0%': {
            transform: 'translate(0, 0) rotate(0deg) scale(1)'
          },
          '25%': {
            transform: 'translate(20px, -20px) rotate(90deg) scale(1.02)'
          },
          '50%': {
            transform: 'translate(0, -30px) rotate(180deg) scale(1.05)'
          },
          '75%': {
            transform: 'translate(-20px, -20px) rotate(270deg) scale(1.02)'
          },
          '100%': {
            transform: 'translate(0, 0) rotate(360deg) scale(1)'
          }
        },
        '@keyframes rotateRingReverse': {
          '0%': {
            transform: 'translate(0, 0) rotate(360deg) scale(1)'
          },
          '25%': {
            transform: 'translate(-20px, 20px) rotate(270deg) scale(1.02)'
          },
          '50%': {
            transform: 'translate(0, 30px) rotate(180deg) scale(1.05)'
          },
          '75%': {
            transform: 'translate(20px, 20px) rotate(90deg) scale(1.02)'
          },
          '100%': {
            transform: 'translate(0, 0) rotate(0deg) scale(1)'
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            radial-gradient(600px circle at 10% 20%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(800px circle at 90% 40%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(700px circle at 15% 70%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(500px circle at 80% 90%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
          `,
          backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%, 30px 30px, 30px 30px',
          backgroundRepeat: 'no-repeat, no-repeat, no-repeat, no-repeat, repeat, repeat',
          backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0, 0 0',
          zIndex: -1,
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(350px circle at 85% 30%, rgba(63, 160, 255, 0.1) 0%, transparent 70%),
            radial-gradient(330px circle at 15% 60%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(400px circle at 50% 90%, rgba(63, 160, 255, 0.06) 0%, transparent 70%)
          `,
          zIndex: -1,
          pointerEvents: 'none',
          animation: 'floatCircle3 30s ease-in-out infinite'
        }
      }}
    >
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: '400px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(63, 160, 255, 0.15)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
              linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
            `,
            backgroundSize: '30px 30px',
            opacity: 0.5,
            zIndex: 0,
          }
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/NB-logo.png"
              alt="Next Basket Logo"
              sx={{
                height: '60px',
                width: 'auto',
                mb: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: 'contrast(1.1)',
                '&:hover': {
                  transform: 'scale(1.05) rotate(-2deg)',
                  filter: 'contrast(1.2) brightness(1.1)'
                },
              }}
            />
            <LockIcon
              sx={{
                fontSize: 40,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 1,
                background: 'linear-gradient(135deg, #000 0%, #333 50%, #000 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Investor Portal Access
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 3,
              }}
            >
              Please enter your password to access the portal
            </Typography>
          </Box>

          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              // Only reset error when user starts typing
              if (error) {
                setError(false);
              }
            }}
            error={error}
            helperText={error ? 'Incorrect password' : ''}
            placeholder="Enter password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              color: '#ffffff',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                color: '#ffffff',
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          >
            Access Portal
          </Button>
        </Box>
      </MotionPaper>
    </Box>
  );
};

export default Login;
